import {
    Box, Stack, TabPanel, Text, VStack,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { faInfoCircle } from '@fortawesome/sharp-light-svg-icons';
import { ThemeTokens } from '@acp/core-ui';
import { ActionsRequiredPopoverContent } from '../actions-required-popover-content';
import { ActionsRequiredGraph } from '../actions-required-graph';
import { PortfolioGraph } from '../portfolio-graph';
import { PortfolioPopoverContent } from '../portfolio-popover-content';
import { useGetStats } from '../../../hooks/use-get-stats';
import { usePropertyManager } from '../../../../agency';
import { CardLayout } from '../../card-layout';
import { useActiveIntegrations } from '../../../../integration';

export function ManagedGraphsTab({
    propertyManagerId,
}: {
    propertyManagerId?: string | null;
}) {
    const { data } = useGetStats(propertyManagerId);
    const { isIntegrated } = useActiveIntegrations();

    const {
        propertyManager,
    } = usePropertyManager(propertyManagerId ?? null);

    const totalPropertiesOnService = useMemo(() => {
        const hideWhenIntegrated = isIntegrated && propertyManagerId === null;
        if (data === undefined || hideWhenIntegrated) {
            return null;
        }

        if (propertyManagerId === null || propertyManagerId === undefined) {
            return `${data.portfolioStats.MANAGED_BY_DI.toLocaleString()} properties on service`;
        }

        // Calculate the total properties on service filtered by a property manager
        // by looking at the sum of the filtered parts
        const propertyManagerPortfolio = data.portfolioStats.BOOKING_IN_PROGRESS
            + data.portfolioStats.COMPLIANT
            + data.portfolioStats.UNRESOLVED_ACTIVE_NOTICES
            + data.portfolioStats.UNPAID_OVERDUE_INVOICES
            + data.portfolioStats.NON_COMPLIANT_QUOTATIONS;

        return `${propertyManagerPortfolio.toLocaleString()} of ${data.portfolioStats.MANAGED_BY_DI.toLocaleString()} total properties on service`;
    }, [data, propertyManagerId, isIntegrated]);

    return (
        <TabPanel as={VStack} alignItems="stretch" p={0} pt={2} gap={4}>
            <Text>
                Compliance summary of properties enrolled with Detector Inspector.
            </Text>
            <VStack alignItems="center" justifyContent="center" gap={4}>
                <CardLayout px={0} py={0}>
                    <Stack
                        direction={{ base: 'column', lg: 'row' }}
                        w="full"
                        gap={{ base: 4, md: 8, lg: 4 }}
                        justifyContent="space-between"
                        alignItems="stretch"
                        flexWrap={{ base: 'wrap', lg: 'nowrap' }}
                    >
                        {/* Portfolio */}
                        <Box
                            shadow="sm"
                            borderWidth={1}
                            borderColor={ThemeTokens.Defaults.borderColor}
                            borderRadius="md"
                            w="full"
                            overflow="hidden"
                        >
                            <CardLayout>
                                <CardLayout.Title>
                                    <CardLayout.Title.Heading
                                        heading={`Portfolio ${propertyManager !== null ? `for ${propertyManager.name}` : ''}`}
                                        position="relative"
                                    >
                                        {
                                            (totalPropertiesOnService !== null && (
                                                <Box alignSelf="start" color="muted.500" position="absolute" top={6}>
                                                    {totalPropertiesOnService}
                                                </Box>
                                            ))
                                        }
                                    </CardLayout.Title.Heading>
                                    <CardLayout.Title.Popover popoverIcon={faInfoCircle}>
                                        <PortfolioPopoverContent />
                                    </CardLayout.Title.Popover>
                                </CardLayout.Title>
                                <CardLayout.Body>
                                    <PortfolioGraph propertyManagerId={propertyManagerId} />
                                </CardLayout.Body>
                            </CardLayout>
                        </Box>

                        {/* Actions required */}
                        <Box
                            shadow="sm"
                            borderWidth={1}
                            borderColor={ThemeTokens.Defaults.borderColor}
                            borderRadius="md"
                            w="full"
                            overflow="hidden"
                        >
                            <CardLayout>
                                <CardLayout.Title>
                                    <CardLayout.Title.Heading
                                        heading={`Actions required ${propertyManager !== null ? `for ${propertyManager.name}` : ''}`}
                                    />
                                    <CardLayout.Title.Popover popoverIcon={faInfoCircle}>
                                        <ActionsRequiredPopoverContent />
                                    </CardLayout.Title.Popover>
                                </CardLayout.Title>
                                <CardLayout.Body>
                                    <ActionsRequiredGraph propertyManagerId={propertyManagerId} />
                                </CardLayout.Body>
                            </CardLayout>
                        </Box>
                    </Stack>
                </CardLayout>
            </VStack>
        </TabPanel>
    );
}
